import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import axios from 'axios';

const API = '/api';

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="tooltip-date">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: ${entry.value?.toLocaleString(undefined, { 
              minimumFractionDigits: 2,
              maximumFractionDigits: entry.value < 1 ? 8 : 2
            })}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Sparkline mini chart for cards
export const Sparkline = ({ data, color = "#00d4aa", height = 40 }) => {
  if (!data || data.length === 0) return null;
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`sparkGradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
            <stop offset="95%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey="price" 
          stroke={color} 
          strokeWidth={1.5}
          fill={`url(#sparkGradient-${color.replace('#', '')})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Main Price Chart Component
const PriceChart = ({ assets = ['bitcoin'], period = '7d', chartType = 'area' }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(period);
  const [selectedAssets, setSelectedAssets] = useState(assets);

  const assetColors = {
    bitcoin: '#f7931a',
    ethereum: '#627eea',
    solana: '#9945ff',
    xrp: '#23292f',
    polkadot: '#e6007a',
    pepe: '#00a86b'
  };

  const assetNames = {
    bitcoin: 'BTC',
    ethereum: 'ETH',
    solana: 'SOL',
    xrp: 'XRP',
    polkadot: 'DOT',
    pepe: 'PEPE'
  };

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/investments/price-history`, {
        params: {
          assets: selectedAssets.join(','),
          days: selectedPeriod === '24h' ? 1 : selectedPeriod === '7d' ? 7 : 30
        }
      });
      
      if (response.data && response.data.data) {
        setChartData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Erro ao carregar dados do gráfico');
      // Generate sample data for demo
      generateSampleData();
    } finally {
      setLoading(false);
    }
  }, [selectedAssets, selectedPeriod]);

  const generateSampleData = () => {
    const days = selectedPeriod === '24h' ? 24 : selectedPeriod === '7d' ? 7 : 30;
    const data = [];
    const basePrice = { bitcoin: 68000, ethereum: 3400, solana: 85 };
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const point = {
        date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        timestamp: date.getTime()
      };
      
      selectedAssets.forEach(asset => {
        const base = basePrice[asset] || 100;
        const variance = (Math.random() - 0.5) * base * 0.1;
        point[asset] = base + variance + (days - i) * (Math.random() - 0.3) * base * 0.01;
      });
      
      data.push(point);
    }
    
    setChartData(data);
  };

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const toggleAsset = (asset) => {
    setSelectedAssets(prev => {
      if (prev.includes(asset)) {
        if (prev.length === 1) return prev; // Keep at least one
        return prev.filter(a => a !== asset);
      }
      return [...prev, asset];
    });
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loader"></div>
        <p>Carregando gráfico...</p>
      </div>
    );
  }

  const ChartComponent = chartType === 'line' ? LineChart : AreaChart;
  const DataComponent = chartType === 'line' ? Line : Area;

  return (
    <div className="price-chart-container" data-testid="price-chart">
      <div className="chart-header">
        <h4>Evolução de Preços</h4>
        <div className="chart-controls">
          <div className="period-selector">
            {['24h', '7d', '30d'].map(p => (
              <button
                key={p}
                className={`period-btn ${selectedPeriod === p ? 'active' : ''}`}
                onClick={() => setSelectedPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="asset-toggles">
        {Object.keys(assetColors).map(asset => (
          <button
            key={asset}
            className={`asset-toggle ${selectedAssets.includes(asset) ? 'active' : ''}`}
            style={{ 
              borderColor: selectedAssets.includes(asset) ? assetColors[asset] : 'transparent',
              color: selectedAssets.includes(asset) ? assetColors[asset] : 'inherit'
            }}
            onClick={() => toggleAsset(asset)}
          >
            {assetNames[asset]}
          </button>
        ))}
      </div>

      {error && <div className="chart-error">{error}</div>}

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          <ChartComponent data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              {selectedAssets.map(asset => (
                <linearGradient key={asset} id={`gradient-${asset}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={assetColors[asset]} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={assetColors[asset]} stopOpacity={0}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
            <XAxis 
              dataKey="date" 
              stroke="#6b7280"
              tick={{ fill: '#6b7280', fontSize: 12 }}
            />
            <YAxis 
              stroke="#6b7280"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickFormatter={(value) => {
                if (value >= 1000) return `$${(value/1000).toFixed(0)}k`;
                return `$${value.toFixed(0)}`;
              }}
              domain={['dataMin - 5%', 'dataMax + 5%']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {selectedAssets.map(asset => (
              chartType === 'line' ? (
                <Line
                  key={asset}
                  type="monotone"
                  dataKey={asset}
                  name={assetNames[asset]}
                  stroke={assetColors[asset]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: assetColors[asset] }}
                />
              ) : (
                <Area
                  key={asset}
                  type="monotone"
                  dataKey={asset}
                  name={assetNames[asset]}
                  stroke={assetColors[asset]}
                  strokeWidth={2}
                  fill={`url(#gradient-${asset})`}
                />
              )
            ))}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PriceChart;
